import React, { useEffect, useRef, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import Paper from "@material-ui/core/Paper";
import { toast } from "react-toastify";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { Grid, LinearProgress, Typography, Box, Chip } from "@material-ui/core";
import api from "../../services/api";
import { has, get, isNull } from "lodash";
import CardCounter from "../../components/Dashboard/CardCounter";
import GroupIcon from "@material-ui/icons/Group";
import ScheduleIcon from "@material-ui/icons/Schedule";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import DoneIcon from "@material-ui/icons/Done";
import DoneAllIcon from "@material-ui/icons/DoneAll";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import ListAltIcon from "@material-ui/icons/ListAlt";
import { useDate } from "../../hooks/useDate";
import usePlans from "../../hooks/usePlans";
import { AuthContext } from "../../context/Auth/AuthContext";

// import { SocketContext } from "../../context/Socket/SocketContext";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(3),
    overflowY: "scroll",
    backgroundColor: theme.palette.background.default,
    border: "none",
    ...theme.scrollbarStyles,
  },

  headerContainer: {
    background: "linear-gradient(90deg, #6517ab 0%, #8e44ad 100%)",
    padding: theme.spacing(4),
    borderRadius: "0 0 30px 30px",
    color: "white",
    marginBottom: theme.spacing(4),
    boxShadow: "0 10px 30px rgba(101, 23, 171, 0.2)",
  },
  title: {
    color: "white",
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
  },
  metricCard: {
    padding: theme.spacing(3),
    borderRadius: "20px",
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 4px 15px rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    transition: "transform 0.3s ease",

    "&:hover": {
      transform: "translateY(-5px)",
    },
  },
  iconContainer: {
    width: "60px",
    height: "60px",
    borderRadius: "15px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "30px",
  },
  progressBar: {
    height: 15,
    borderRadius: 10,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    "& .MuiLinearProgress-bar": {
      borderRadius: 10,
      background: "linear-gradient(90deg, #25D366 0%, #128C7E 100%)",
    },
  },
}));

const CampaignReport = () => {
  const theme = useTheme();
  const classes = useStyles();

  const history = useHistory();

  const { campaignId } = useParams();

  const [campaign, setCampaign] = useState({});
  const [validContacts, setValidContacts] = useState(0);
  const [delivered, setDelivered] = useState(0);
  const [confirmationRequested, setConfirmationRequested] = useState(0);
  const [confirmed, setConfirmed] = useState(0);
  const [percent, setPercent] = useState(0);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);
  //   const socketManager = useContext(SocketContext);
  const { user, socket } = useContext(AuthContext);


  const { datetimeToClient } = useDate();
  const { getPlanCompany } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const companyId = user.companyId;
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useCampaigns) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mounted.current) {
      findCampaign();
    }

    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mounted.current && has(campaign, "shipping")) {
      if (has(campaign, "contactList")) {
        const contactList = get(campaign, "contactList");
        const valids = contactList.contacts.filter((c) => c.isWhatsappValid);
        setValidContacts(valids.length);
      }

      if (has(campaign, "shipping")) {
        const contacts = get(campaign, "shipping");
        const delivered = contacts.filter((c) => !isNull(c.deliveredAt));
        const confirmationRequested = contacts.filter(
          (c) => !isNull(c.confirmationRequestedAt)
        );
        const confirmed = contacts.filter(
          (c) => !isNull(c.deliveredAt) && !isNull(c.confirmationRequestedAt)
        );
        setDelivered(delivered.length);
        setConfirmationRequested(confirmationRequested.length);
        setConfirmed(confirmed.length);
        setDelivered(delivered.length);
      }
    }
  }, [campaign]);

  useEffect(() => {
    setPercent((delivered / validContacts) * 100);
  }, [delivered, validContacts]);

  useEffect(() => {
    const companyId = user.companyId;
    // const socket = socketManager.GetSocket();

    const onCampaignEvent = (data) => {

      if (data.record.id === +campaignId) {
        setCampaign(data.record);

        if (data.record.status === "FINALIZADA") {
          setTimeout(() => {
            findCampaign();
          }, 5000);
        }
      }
    };
    socket.on(`company-${companyId}-campaign`, onCampaignEvent);

    return () => {
      socket.off(`company-${companyId}-campaign`, onCampaignEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const findCampaign = async () => {
    setLoading(true);
    const { data } = await api.get(`/campaigns/${campaignId}`);
    setCampaign(data);
    setLoading(false);
  };

  const formatStatus = (val) => {
    switch (val) {
      case "INATIVA":
        return i18n.t("campaignReport.inactive");
      case "PROGRAMADA":
        return i18n.t("campaignReport.scheduled");
      case "EM_ANDAMENTO":
        return i18n.t("campaignReport.process");
      case "CANCELADA":
        return i18n.t("campaignReport.cancelled");
      case "FINALIZADA":
        return i18n.t("campaignReport.finished");
      default:
        return val;
    }
  };

  return (
    <MainContainer>
      <div className={classes.headerContainer}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} sm={8}>
            <Typography variant="h4" className={classes.title}>
              {campaign.name || i18n.t("campaignReport.campaign")}
            </Typography>
            <Box display="flex" alignItems="center" gap="10px">
              <Chip 
                label={formatStatus(campaign.status)} 
                style={{ 
                  backgroundColor: "rgba(255,255,255,0.2)", 
                  color: "white", 
                  fontWeight: "bold" 
                }} 
              />
              <Typography variant="body1" style={{ opacity: 0.9 }}>
                {delivered} de {validContacts} contatos entregues
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box textAlign="right">
              <Typography variant="h3" style={{ fontWeight: 700 }}>
                {percent.toFixed(0)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={percent}
                className={classes.progressBar}
              />
            </Box>
          </Grid>
        </Grid>
      </div>
      <div className={classes.mainPaper}>
        <Grid spacing={3} container>
          <Grid xs={12} md={4} item>
            <div className={classes.metricCard}>
              <div className={classes.iconContainer} style={{ backgroundColor: theme.palette.type === 'light' ? "#e8f5e9" : "rgba(46, 125, 50, 0.2)", color: "#2e7d32" }}>
                <GroupIcon fontSize="inherit" />
              </div>

              <div>
                <Typography variant="caption" color="textSecondary">Contatos Válidos</Typography>
                <Typography variant="h5" style={{ fontWeight: 700 }}>{validContacts}</Typography>
              </div>
            </div>
          </Grid>

          <Grid xs={12} md={4} item>
            <div className={classes.metricCard}>
              <div className={classes.iconContainer} style={{ backgroundColor: theme.palette.type === 'light' ? "#e3f2fd" : "rgba(25, 118, 210, 0.2)", color: "#1976d2" }}>
                <CheckCircleIcon fontSize="inherit" />
              </div>

              <div>
                <Typography variant="caption" color="textSecondary">Entregues</Typography>
                <Typography variant="h5" style={{ fontWeight: 700 }}>{delivered}</Typography>
              </div>
            </div>
          </Grid>

          {campaign.confirmation && (
            <>
              <Grid xs={12} md={4} item>
                <div className={classes.metricCard}>
                  <div className={classes.iconContainer} style={{ backgroundColor: theme.palette.type === 'light' ? "#fff3e0" : "rgba(239, 108, 0, 0.2)", color: "#ef6c00" }}>
                    <DoneIcon fontSize="inherit" />
                  </div>

                  <div>
                    <Typography variant="caption" color="textSecondary">Confirmações Solicitadas</Typography>
                    <Typography variant="h5" style={{ fontWeight: 700 }}>{confirmationRequested}</Typography>
                  </div>
                </div>
              </Grid>
              <Grid xs={12} md={4} item>
                <div className={classes.metricCard}>
                  <div className={classes.iconContainer} style={{ backgroundColor: theme.palette.type === 'light' ? "#f3e5f5" : "rgba(101, 23, 171, 0.2)", color: "#6517ab" }}>
                    <DoneAllIcon fontSize="inherit" />
                  </div>

                  <div>
                    <Typography variant="caption" color="textSecondary">Confirmados</Typography>
                    <Typography variant="h5" style={{ fontWeight: 700 }}>{confirmed}</Typography>
                  </div>
                </div>
              </Grid>
            </>
          )}

          <Grid xs={12} md={4} item>
            <div className={classes.metricCard}>
              <div className={classes.iconContainer} style={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.secondary }}>

                <WhatsAppIcon fontSize="inherit" />
              </div>
              <div>
                <Typography variant="caption" color="textSecondary">Conexão</Typography>
                <Typography variant="h6" style={{ fontWeight: 600 }}>{campaign.whatsapp?.name || "N/A"}</Typography>
              </div>
            </div>
          </Grid>

          <Grid xs={12} md={4} item>
            <div className={classes.metricCard}>
              <div className={classes.iconContainer} style={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.secondary }}>

                <ListAltIcon fontSize="inherit" />
              </div>
              <div>
                <Typography variant="caption" color="textSecondary">Lista de Contatos</Typography>
                <Typography variant="h6" style={{ fontWeight: 600 }}>{campaign.contactList?.name || "N/A"}</Typography>
              </div>
            </div>
          </Grid>

          <Grid xs={12} md={4} item>
            <div className={classes.metricCard}>
              <div className={classes.iconContainer} style={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.secondary }}>

                <ScheduleIcon fontSize="inherit" />
              </div>
              <div>
                <Typography variant="caption" color="textSecondary">Agendamento</Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>{datetimeToClient(campaign.scheduledAt)}</Typography>
              </div>
            </div>
          </Grid>

          <Grid xs={12} md={4} item>
            <div className={classes.metricCard}>
              <div className={classes.iconContainer} style={{ backgroundColor: "#f5f5f5", color: "#616161" }}>
                <EventAvailableIcon fontSize="inherit" />
              </div>
              <div>
                <Typography variant="caption" color="textSecondary">Conclusão</Typography>
                <Typography variant="body2" style={{ fontWeight: 600 }}>{datetimeToClient(campaign.completedAt)}</Typography>
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
    </MainContainer>
  );
};

export default CampaignReport;
